import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import type { DashboardState, TimePeriod } from '../types';
import { dashboardService } from '../api/dashboardService';
import { switchWorkspace } from '@/features/workspace/workspaceSlice';

export const fetchUpcomingWebinars = createAsyncThunk(
  'dashboard/fetchWebinars',
  async (workspaceId: number) => {
    return await dashboardService.getUpcomingWebinars(workspaceId);
  },
);

export const fetchDashboardStats = createAsyncThunk(
  'dashboard/fetchStats',
  async ({ workspaceId, period }: { workspaceId: number; period: TimePeriod }) => {
    let from: string | undefined;
    const to = DateTime.now().endOf('day').toISO() || undefined;

    if (period === 'WEEK') {
      from = DateTime.now().startOf('week').toISO() || undefined;
    } else if (period === 'MONTH') {
      from = DateTime.now().startOf('month').toISO() || undefined;
    } else if (period === 'YEAR') {
      from = DateTime.now().startOf('year').toISO() || undefined;
    }

    return await dashboardService.getStats(workspaceId, from, to);
  },
);

const initialState: DashboardState = {
  webinars: { data: [], loading: false, error: null },
  stats: { data: null, period: 'TOTAL', loading: false, error: null },
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setStatsPeriod(state, action: PayloadAction<TimePeriod>) {
      state.stats.period = action.payload;
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUpcomingWebinars.pending, (state) => {
        state.webinars.loading = true;
      })
      .addCase(fetchUpcomingWebinars.fulfilled, (state, action) => {
        state.webinars.loading = false;
        state.webinars.data = action.payload;
      })
      .addCase(fetchDashboardStats.pending, (state) => {
        state.stats.loading = true;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.stats.loading = false;
        state.stats.data = action.payload;
      })
      .addCase(switchWorkspace, () => initialState);
  },
});

export const { setStatsPeriod, reset: resetDashboard } = dashboardSlice.actions;
export default dashboardSlice.reducer;
